import React from 'react';
import { StepOne } from './steps/StepOne';
import { StepTwo } from './steps/StepTwo';
import { StepThree } from './steps/StepThree';
import { StepFour } from './steps/StepFour';
import { BudgetSummaryBox } from './BudgetSummaryBox';
import { Calculator, PiggyBank, ArrowRight, ArrowLeft, TrendingUp, Globe2, Sparkles } from 'lucide-react';
import { Country } from '../../types/todo';

interface BudgetFormProps {
  countries: Country[];
  onSubmit: (data: any) => void;
  step: number;
  onBack: () => void;
  onFormDataChange: (data: any) => void;
  formData: any;
  housingTypes: any[];
  currency: string;
  city?: string;
  countryName?: string;
}

export function BudgetForm({
  countries,
  onSubmit,
  step,
  onBack,
  onFormDataChange,
  formData,
  housingTypes,
  currency,
  city,
  countryName
}: BudgetFormProps) {
  const [includePreDeparture, setIncludePreDeparture] = React.useState(() => {
    const saved = localStorage.getItem('includePreDeparture');
    return saved === null ? true : saved === 'true';
  });

  const isStepOneValid = React.useMemo(() => {
    return (
      formData.country &&
      formData.city &&
      formData.housingType &&
      formData.initialSavings &&
      formData.pvtDuration
    );
  }, [formData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && !isStepOneValid) return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      onSubmit(formData);
    }, 500);
  };

  const stepLabels = ['Configuration', 'Avant le depart', 'Budget mensuel', 'Installation'];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-8">
      <div className="xl:col-span-3 relative space-y-6">
        {step === 1 && (
          <div className="relative overflow-hidden rounded-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-500/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
            </div>

            <div className="relative z-10 p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-1.5 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/10">
                  <Sparkles className="w-3.5 h-3.5 text-accent-400" />
                  <span className="text-xs font-medium text-accent-300">Simulateur intelligent</span>
                </div>
              </div>

              <h2 className="text-xl sm:text-3xl font-bold text-white mb-2">
                Simulateur de budget PVT
              </h2>
              <p className="text-sm sm:text-base text-gray-300 max-w-lg">
                Estimez votre budget en 4 etapes, avec des donnees en temps reel
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                  <div className="p-2 bg-primary-500/20 rounded-lg">
                    <Globe2 className="w-4 h-4 text-primary-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Couverture</p>
                    <p className="text-sm font-semibold text-white">30+ pays</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                  <div className="p-2 bg-accent-500/20 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-accent-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Donnees</p>
                    <p className="text-sm font-semibold text-white">Temps reel</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <PiggyBank className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Precision</p>
                    <p className="text-sm font-semibold text-white">API Numbeo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {step === 1 && (
            <StepOne
              formData={formData}
              countries={countries}
              onFormDataChange={onFormDataChange}
            />
          )}
          {step === 2 && (
            <StepTwo
              formData={formData}
              onFormDataChange={onFormDataChange}
            />
          )}
          {step === 3 && (
            <StepThree
              formData={formData}
              onFormDataChange={onFormDataChange}
            />
          )}
          {step === 4 && (
            <StepFour
              formData={formData}
              onFormDataChange={onFormDataChange}
            />
          )}

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 p-4 sm:p-6 shadow-premium">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setTimeout(() => { onBack(); }, 500);
                  }}
                  className="order-2 sm:order-1 w-full sm:w-auto group flex items-center justify-center gap-2.5 px-5 py-3 text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 border border-gray-200 dark:border-gray-600"
                >
                  <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  <span className="font-medium text-sm">Retour</span>
                </button>
              ) : (
                <div />
              )}

              <button
                type="submit"
                disabled={step === 1 && !isStepOneValid}
                className="order-1 sm:order-2 w-full sm:w-auto group flex items-center justify-center gap-2.5 px-8 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-xl hover:shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:shadow-none transform hover:scale-[1.02] disabled:hover:scale-100"
              >
                <span>{step === 4 ? 'Calculer mon budget' : stepLabels[step] ? `Continuer` : 'Continuer'}</span>
                {step !== 4 ? (
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                ) : (
                  <Calculator className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="hidden xl:block xl:col-span-2 relative">
        <div className="sticky top-[8.5rem] pt-8">
          <BudgetSummaryBox
            formData={formData}
            housingTypes={housingTypes}
            currency={currency}
            city={city}
            countryName={countryName}
            includePreDeparture={includePreDeparture}
            onTogglePreDeparture={() => setIncludePreDeparture(!includePreDeparture)}
            currentStep={step}
          />
        </div>
      </div>
    </div>
  );
}